import React, { useState, useEffect } from 'react';
import { Layout, Menu, message, Spin, Card, Input, Button, List, Tag, Space, Typography } from 'antd';
import { UploadOutlined, SearchOutlined, FileTextOutlined, HeartOutlined, RobotOutlined, HomeOutlined, InfoCircleOutlined } from '@ant-design/icons';
import DocumentUpload from './components/DocumentUpload';
import DocumentList from './components/DocumentList';
import DomainSelector from './components/DomainSelector';
import RAGChat from './components/RAGChat';
import HomePage from './components/HomePage';
import DetailPage from './components/DetailPage';
import ChatbotPage from './components/ChatbotPage';
import DemoApp from './demo_App';
import JiraTicketForm from './components/JiraTicketForm';
import { ragApi } from './services/ragApi';
import './App.css';

const { Header, Content, Sider } = Layout;
const { Search } = Input;
const { Paragraph, Text } = Typography;

interface SearchResult {
  title?: string;
  content?: string;
  article_title?: string;
  article_content?: string;
  article_id?: string;
  similarity: number;
  document_type?: string;
  author?: string;
  dynasty?: string;
  artist?: string;
}

enum SearchType {
  Normal = 'normal',
  Rerank = 'rerank'
}

const App: React.FC = () => {
  const [selectedKey, setSelectedKey] = useState('home');
  
  // 添加調試日誌
  const handleSetSelectedKey = (key: string) => {
    console.log('🔄 setSelectedKey called with:', key);
    console.log('🔄 Current selectedKey:', selectedKey);
    setSelectedKey(key);
    console.log('✅ setSelectedKey completed');
  };
  const [loading, setLoading] = useState(false);
  const [systemHealth, setSystemHealth] = useState<any>(null);
  const [selectedDomain, setSelectedDomain] = useState<string>('');
  const [domainTitle, setDomainTitle] = useState<string>('');
  const [showChat, setShowChat] = useState(false);
  const [initialQuery, setInitialQuery] = useState<string>('');
  
  // 搜索相关状态
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchType, setSearchType] = useState<SearchType>(SearchType.Normal);

  useEffect(() => {
    checkSystemHealth();
  }, []);

  const checkSystemHealth = async () => {
    try {
      const health = await ragApi.healthCheck();
      setSystemHealth(health);
      setLoading(false);
    } catch (error) {
      console.error('Health check failed:', error);
      message.error('系統連接失敗，請檢查後端服務');
      setLoading(false);
    }
  };

  const menuItems = [
    {
      key: 'home',
      icon: <HomeOutlined />,
      label: '首頁',
    },
    {
      key: 'detail',
      icon: <InfoCircleOutlined />,
      label: '詳細信息',
    },
    {
      key: 'upload',
      icon: <UploadOutlined />,
      label: '上傳文檔',
    },
    {
      key: 'search',
      icon: <SearchOutlined />,
      label: '文檔搜索',
    },
    {
      key: 'manage',
      icon: <FileTextOutlined />,
      label: '文檔管理',
    },
    {
      key: 'smart-query',
      icon: <RobotOutlined />,
      label: 'AI智能詢問',
    },
    {
      key: 'rag-chat',
      icon: <RobotOutlined />,
      label: 'RAG智能對話',
    },
    {
      key: 'jira',
      icon: <HeartOutlined />,
      label: 'Jira Ticket',
    },
  ];

  const handleStartChatFromHome = (query: string) => {
    // 設置預設領域為古董文物
    setSelectedDomain('antiques');
    setDomainTitle('古董文物');
    setShowChat(true);
    setSelectedKey('smart-query');
    
    // 這裡可以將初始查詢傳遞給AIChat組件
    // 暫時存儲查詢內容，稍後傳遞給AIChat
    setInitialQuery(query);
  };

  const handleDomainSelect = (domain: string, title: string) => {
    setSelectedDomain(domain);
    setDomainTitle(title);
    setShowChat(true);
  };

  // 搜索处理函数
  const handleSearch = async (query: string) => {
    if (!query.trim()) {
      message.warning('請輸入搜索關鍵詞');
      return;
    }

    setSearchLoading(true);
    try {
      let response;
      if (searchType === SearchType.Normal) {
        response = await ragApi.searchDocuments({ query, top_k: 10 });
        message.success(`找到 ${response.results?.length || 0} 個相關結果 (普通搜索)`);
      } else {
        response = await ragApi.searchAndRerankDocuments({ query, top_k: 10 });
        message.success(`找到 ${response.results?.length || 0} 個相關結果 (AI重排序)`);
      }
      setSearchResults(response.results || []);
    } catch (error: any) {
      console.error('Search failed:', error);
      message.error(`搜索失敗: ${error.response?.data?.detail || error.message}`);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleBackToDomainSelector = () => {
    setShowChat(false);
    setSelectedDomain('');
    setDomainTitle('');
    setInitialQuery(''); // 清空初始查詢
  };

  const handleNavigateToDemo = () => {
    setSelectedKey('demo');
  };

  const renderContent = () => {
    switch (selectedKey) {
      case 'home':
        return <HomePage onNavigate={handleSetSelectedKey} />;
      case 'chatbot':
        return <ChatbotPage />;
      case 'demo':
        return <DemoApp />;
      case 'detail':
        return <DetailPage onBack={() => setSelectedKey('home')} />;
      case 'upload':
        return <DocumentUpload onUploadSuccess={() => {
          message.success('文檔上傳成功！');
        }} />;
      case 'search':
        return renderSearchContent();
      case 'manage':
        return <DocumentList />;
      case 'smart-query':
        if (showChat && selectedDomain) {
          return (
            <RAGChat 
              selectedDomain={selectedDomain}
              domainTitle={domainTitle}
              onBack={handleBackToDomainSelector}
              initialQuery={initialQuery}
            />
          );
        }
        return (
          <DomainSelector onDomainSelect={handleDomainSelect} />
        );
      case 'rag-chat':
        if (showChat && selectedDomain) {
          return (
            <RAGChat 
              selectedDomain={selectedDomain}
              domainTitle={domainTitle}
              onBack={handleBackToDomainSelector}
              initialQuery={initialQuery}
            />
          );
        }
        return (
          <DomainSelector onDomainSelect={handleDomainSelect} />
        );
      case 'jira':
        return <JiraTicketForm />;
      default:
        return <HomePage onNavigate={handleSetSelectedKey} />;
    }
  };

  const renderSearchContent = () => (
    <div style={{ padding: '24px' }}>
      <Card title="智能文檔搜索">
        <div style={{ marginBottom: '16px' }}>
          <Space.Compact>
            <Button 
              type={searchType === SearchType.Normal ? 'primary' : 'default'}
              onClick={() => setSearchType(SearchType.Normal)}
            >
              普通搜索
            </Button>
            <Button 
              type={searchType === SearchType.Rerank ? 'primary' : 'default'}
              onClick={() => setSearchType(SearchType.Rerank)}
            >
              AI重排序搜索
            </Button>
          </Space.Compact>
          <div style={{ marginTop: '8px' }}>
            <Text type="secondary">
              {searchType === SearchType.Normal ? 
                '普通搜索：基於向量相似度的搜索結果' :
                'AI重排序搜索：使用大語言模型對搜索結果進行重排序，提高相關性'}
            </Text>
          </div>
        </div>
        <Search
          placeholder="輸入搜索關鍵詞，如：唐代書法、宋代繪畫、古代印章..."
          allowClear
          enterButton="搜索"
          size="large"
          onSearch={handleSearch}
          loading={searchLoading}
        />
      </Card>

      {searchResults.length > 0 && (
        <Card title={`搜索結果 (${searchResults.length})`}>
          <List
            itemLayout="vertical"
            dataSource={searchResults}
            renderItem={(item, index) => (
              <List.Item
                key={index}
                extra={
                  <div>
                    <Tag color="blue">相似度: {(item.similarity * 100).toFixed(1)}%</Tag>
                    {item.document_type && <Tag>{item.document_type}</Tag>}
                    {item.dynasty && <Tag color="gold">{item.dynasty}</Tag>}
                  </div>
                }
              >
                <List.Item.Meta
                  title={item.article_title || item.title}
                  description={
                    <div>
                      {item.author && <Text type="secondary">作者: {item.author} | </Text>}
                      {item.artist && <Text type="secondary">藝術家: {item.artist} | </Text>}
                    </div>
                  }
                />
                <Paragraph ellipsis={{ rows: 2, expandable: true }}>
                  {item.article_content || item.content}
                </Paragraph>
              </List.Item>
            )}
          />
        </Card>
      )}
    </div>
  );

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh' 
      }}>
        <Spin size="large">
          <div style={{ padding: '50px' }}>正在連接系統...</div>
        </Spin>
      </div>
    );
  }

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Content
        style={{
          background: '#fff',
          padding: 24,
          margin: 0,
          minHeight: '100vh',
        }}
      >
        {renderContent()}
      </Content>
    </Layout>
  );
};

export default App;